import type { Context } from "configureStore";
import type { ApplicationState } from "reduxStore/rootReducer";
import {
	createAsyncThunk,
	createSlice,
	type PayloadAction,
	type SerializedError,
} from "@reduxjs/toolkit";
import { produce } from "immer";
import { forEach } from "lodash";
import type { GroupByOptions } from "types/Report";
import type { RawResult, ResultDto } from "types/Result";

export interface ResultsState {
	readonly results: Record<string, ResultDto>;
	readonly states: Record<
		string,
		{
			isLoading: boolean;
			hasLoaded: boolean;
			error: SerializedError | null;
		}
	>;
}

export interface LoadResultsParams {
	requestId: string;
	organizationId: string;
	reportId: string;
	groupBy: GroupByOptions;
	segmentPath: string | null;
	accessToken?: string;
	forceReload?: boolean;
	limitToResultIds?: string[] | null;
	commentLimit: number;
}

const loadResults = createAsyncThunk(
	"results/loadResults",
	async (params: LoadResultsParams, thunkAPI) => {
		const hasResultForRequest = hasLoadedResultsSelector(
			thunkAPI.getState() as ApplicationState,
			params.requestId,
		);
		if (hasResultForRequest && !params.forceReload) {
			const result = getResultForRequestSelector(
				thunkAPI.getState() as ApplicationState,
				params.requestId,
			);
			if (!result) {
				throw new Error(`Result not found: ${params.requestId}`);
			}
			return {
				requestId: params.requestId,
				segmentPath: params.segmentPath ?? "GLOBAL",
				result,
			};
		}
		const extra = thunkAPI.extra as Context;
		const { data } = await extra.api.get<ResultDto>(
			`/organizations/${params.organizationId}/reports/${params.reportId}/results`,
			{
				params: {
					segmentPath: params.segmentPath ?? "GLOBAL",
					groupBy: params.groupBy,
					limitToResultIds: params.limitToResultIds,
					limit: params.commentLimit,
				},
			},
		);
		return {
			requestId: params.requestId,
			segmentPath: params.segmentPath ?? "GLOBAL",
			result: data,
		};
	},
);

export interface LoadSharedResultsParams {
	requestId: string;
	accessToken: string;
	segmentPath: string | null;
	groupBy: GroupByOptions;
	forceReload?: boolean;
	limitToResultIds?: string[] | null;
}

const loadSharedResults = createAsyncThunk(
	"results/loadSharedResults",
	async (params: LoadSharedResultsParams, thunkAPI) => {
		const hasResultForRequest = hasLoadedResultsSelector(
			thunkAPI.getState() as ApplicationState,
			params.requestId,
		);
		if (hasResultForRequest && !params.forceReload) {
			const result = getResultForRequestSelector(
				thunkAPI.getState() as ApplicationState,
				params.requestId,
			);
			if (!result) {
				throw new Error(`Result not found: ${params.requestId}`);
			}
			return {
				requestId: params.requestId,
				segmentPath: params.segmentPath ?? "GLOBAL",
				result,
			};
		}
		const extra = thunkAPI.extra as Context;
		const { data } = await extra.api.get<ResultDto>(
			"public/shared-report/results",
			{
				params: {
					segmentPath: params.segmentPath ?? "GLOBAL",
					accessToken: params.accessToken,
					groupBy: params.groupBy,
					limitToResultIds: params.limitToResultIds,
				},
			},
		);
		return {
			requestId: params.requestId,
			segmentPath: params.segmentPath ?? "GLOBAL",
			result: data,
		};
	},
);

const { reducer, actions: sliceActions } = createSlice({
	name: "results",
	initialState: {
		results: {},
		states: {},
	} as ResultsState,
	reducers: {
		setResult(
			state,
			action: PayloadAction<{
				requestId: string;
				segmentPath: string;
				result: ResultDto;
			}>,
		) {
			state.results[action.payload.requestId] = {
				...action.payload.result,
				segmentPath: action.payload.segmentPath,
			};
			state.states[action.payload.requestId] = {
				isLoading: false,
				hasLoaded: true,
				error: null,
			};
		},
		setResultLoading(state, action: PayloadAction<{ requestId: string }>) {
			if (!state.states[action.payload.requestId]) {
				state.states[action.payload.requestId] = {
					isLoading: true,
					hasLoaded: false,
					error: null,
				};
			} else {
				state.states[action.payload.requestId].isLoading = true;
				state.states[action.payload.requestId].error = null;
			}
		},
		setResultError(
			state,
			action: PayloadAction<{
				requestId: string;
				error: SerializedError;
			}>,
		) {
			state.states[action.payload.requestId] = {
				isLoading: false,
				hasLoaded: false,
				error: action.payload.error,
			};
		},
	},
	extraReducers(builder) {
		builder.addCase(loadResults.pending, (state, action) => {
			if (!state.states[action.meta.arg.requestId]) {
				state.states[action.meta.arg.requestId] = {
					error: null,
					hasLoaded: false,
					isLoading: true,
				};
			} else {
				state.states[action.meta.arg.requestId].isLoading = true;
				state.states[action.meta.arg.requestId].error = null;
			}
		});
		builder.addCase(loadResults.rejected, (state, action) => {
			state.states[action.meta.arg.requestId].isLoading = false;
			state.states[action.meta.arg.requestId].hasLoaded = false;
			state.states[action.meta.arg.requestId].error = action.error;
		});
		const addRoundIdToValues = (results: ResultDto) => {
			const rawResults = produce(results as unknown as RawResult, (draft) => {
				forEach(draft.values, (valueResult) => {
					valueResult.values = valueResult.values.map(({ _id, ...value }) => ({
						_id,
						...value,
						roundId: _id,
					}));
				});
			});
			return rawResults as unknown as ResultDto;
		};
		builder.addCase(loadResults.fulfilled, (state, action) => {
			state.states[action.meta.arg.requestId].isLoading = false;
			state.states[action.meta.arg.requestId].hasLoaded = true;
			state.states[action.meta.arg.requestId].error = null;
			const rawResults = addRoundIdToValues(action.payload.result);
			state.results[action.payload.requestId] = {
				...rawResults,
				segmentPath: action.payload.segmentPath,
			};
		});

		builder.addCase(loadSharedResults.pending, (state, action) => {
			if (!state.states[action.meta.arg.requestId]) {
				state.states[action.meta.arg.requestId] = {
					error: null,
					hasLoaded: false,
					isLoading: true,
				};
			} else {
				state.states[action.meta.arg.requestId].isLoading = true;
				state.states[action.meta.arg.requestId].error = null;
			}
		});
		builder.addCase(loadSharedResults.rejected, (state, action) => {
			state.states[action.meta.arg.requestId].isLoading = false;
			state.states[action.meta.arg.requestId].hasLoaded = false;
			state.states[action.meta.arg.requestId].error = action.error;
		});
		builder.addCase(loadSharedResults.fulfilled, (state, action) => {
			state.states[action.meta.arg.requestId].isLoading = false;
			state.states[action.meta.arg.requestId].hasLoaded = true;
			state.states[action.meta.arg.requestId].error = null;
			const rawResults = addRoundIdToValues(action.payload.result);
			state.results[action.payload.requestId] = {
				...rawResults,
				segmentPath: action.payload.segmentPath,
			};
		});
	},
});

export default reducer;
export const actions = {
	loadResults,
	loadSharedResults,
	...sliceActions,
};

export const isLoadingResultsSelector = (
	state: ApplicationState,
	resultRequestId: string,
): boolean => state.results.states[resultRequestId]?.isLoading || false;

export const hasLoadedResultsSelector = (
	state: ApplicationState,
	resultRequestId: string,
): boolean => state.results.states[resultRequestId]?.hasLoaded || false;

export const hasLoadResultsErrorSelector = (
	state: ApplicationState,
	resultRequestId: string,
): boolean => !!state.results.states[resultRequestId]?.error;

const getResultForRequestSelector = (
	state: ApplicationState,
	resultRequestId: string,
): ResultDto | null => state.results.results[resultRequestId] || null;
